import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  DataObject as DataObjectIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import homeBanner from '../assets/images/home-banner.png';
import { FREE_TRIAL_ENABLED } from '../services/config';
import { useSystemConfig } from '../hooks/useSystemConfig';

const features = [
  {
    icon: <FunctionsIcon sx={{ fontSize: 48 }} />,
    title: 'AI Functions',
    description: 'Create powerful AI-powered functions with natural language instructions',
  },
  {
    icon: <DataObjectIcon sx={{ fontSize: 48 }} />,
    title: 'Custom Objects',
    description: 'Build structured data models that AI can understand and populate',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 48 }} />,
    title: 'Lightning Fast',
    description: 'Optimized for performance with automatic scaling and caching',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Enterprise Security',
    description: 'Bank-level security with Firebase auth and role-based access control',
  },
  {
    icon: <CloudIcon sx={{ fontSize: 48 }} />,
    title: 'Fully Managed',
    description: 'Deployed on Google Cloud with 99.9% uptime SLA',
  },
  {
    icon: <AIIcon sx={{ fontSize: 48 }} />,
    title: 'Multi-Model Support',
    description: 'Use GPT-4, Claude, Gemini, and more with automatic fallback',
  },
];

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { signup_credits: signupCredits } = useSystemConfig();

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Show nothing while checking auth status
  if (loading) {
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Easy AI Platform
          </Typography>
          <Button color="inherit" component={Link} to="/login">
            Sign In
          </Button>
          <Button 
            variant="contained" 
            component={Link} 
            to="/signup"
            sx={{ ml: 2 }}
          >
            Get Started
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                Build AI-Powered Applications
              </Typography>
              <Typography variant="h5" sx={{ mb: FREE_TRIAL_ENABLED ? 3 : 4, opacity: 0.9 }}>
                Create custom AI functions and data structures with our intuitive platform.
                No complex setup required.
              </Typography>
              {FREE_TRIAL_ENABLED && (
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.15)', 
                  borderRadius: 2, 
                  p: 2, 
                  mb: 3,
                  backdropFilter: 'blur(10px)'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    🎉 Get {signupCredits.toLocaleString()} Free AI Credits
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Start building immediately with no credit card required
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  {FREE_TRIAL_ENABLED ? 'Get Free Credits' : 'Start Free Trial'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={homeBanner}
                alt="AI Platform - Neural Network Visualization"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold">
          Everything You Need to Build AI Apps
        </Typography>
        <Typography variant="h6" align="center" color="textSecondary" sx={{ mb: 6 }}>
          Powerful features designed for developers and businesses
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Ready to Get Started?
              </Typography>
              <Typography variant="h6" color="textSecondary" sx={{ mb: FREE_TRIAL_ENABLED ? 2 : 4 }}>
                Join thousands of developers building the next generation of AI applications
              </Typography>
              {FREE_TRIAL_ENABLED && (
                <Box sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.08)', 
                  borderRadius: 2, 
                  p: 2, 
                  mb: 3,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    🚀 Sign up and get {signupCredits.toLocaleString()} free AI credits instantly!
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    No credit card required • Start building in seconds
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{ px: 4, py: 1.5 }}
              >
                Create Free Account
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'background.paper', py: 4, borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom align="center">
                Easy AI Platform
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Building the future of AI-powered applications
              </Typography>
            </Grid>
          </Grid>
          
          {/* There's an AI for That Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <a 
              href="https://theresanaiforthat.com/ai/platform-admin-portal/?ref=featured&v=440175" 
              target="_blank" 
              rel="nofollow noreferrer"
              style={{ display: 'inline-block' }}
            >
              <img 
                width="300" 
                src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
                alt="Featured on There's an AI for That"
                style={{ display: 'block' }}
              />
            </a>
          </Box>

          {/* Legal Links */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, mb: 2 }}>
            <Typography 
              component="a" 
              href="https://www.passio.ai/terms-of-use" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="body2"
              sx={{ 
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { 
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
            >
              Terms of Service
            </Typography>
            <Typography variant="body2" color="textSecondary">•</Typography>
            <Typography 
              component="a" 
              href="https://www.passio.ai/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="body2"
              sx={{ 
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { 
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
            >
              Privacy Policy
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary" align="center">
            © {new Date().getFullYear()} Easy AI Platform. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
